import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { API_ENDPOINTS } from '../config/api';

interface User {
  email: string;
  full_name: string;
  organization: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsSetup: boolean;
  checkSetupStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  const checkSetupStatus = useCallback(async () => {
    try {
      // Sign-up is only for a database that has no admin yet.
      // A 500 or empty body means the API is unreachable, not that
      // admin@inuka.ai is missing.
      const setupResponse = await fetch(API_ENDPOINTS.AUTH.SETUP_STATUS, { cache: 'no-store' });
      const rawBody = await setupResponse.text();
      let setupData: { needs_setup?: boolean } | null = null;
      try {
        setupData = rawBody ? JSON.parse(rawBody) : null;
      } catch {
        setupData = null;
      }

      if (setupResponse.ok && setupData?.needs_setup === true) {
        setNeedsSetup(true);
        setIsLoading(false);
        return;
      }

      // System has admin users, check for existing token
      const savedToken = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('auth_user');

      if (savedToken && savedUser) {
        setToken(savedToken);
        setUser(JSON.parse(savedUser));
        // Verify token is still valid
        await verifyToken(savedToken);
      }
      setNeedsSetup(false);
    } catch (error) {
      console.error('Setup check failed:', error);
      setNeedsSetup(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Session timeout - 10 minutes of inactivity
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const resetTimeout = () => {
      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout for 10 minutes (600000 ms)
      if (user && token) {
        timeoutId = setTimeout(() => {
          console.log('Session expired due to inactivity');
          logout();
          alert('Session expired due to inactivity. Please login again.');
        }, 600000); // 10 minutes
      }
    };

    // Activity events to reset timeout
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    activityEvents.forEach(event => {
      window.addEventListener(event, resetTimeout);
    });

    // Initialize timeout
    resetTimeout();

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      activityEvents.forEach(event => {
        window.removeEventListener(event, resetTimeout);
      });
    };
  }, [user, token]);

  useEffect(() => {
    // Check setup status first, then check for existing token
    checkSetupStatus();
  }, [checkSetupStatus]);

  const verifyToken = async (authToken: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.VERIFY, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Token is invalid, clear auth state
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.AUTH.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const { token: authToken, user: userData } = data;

        setToken(authToken);
        setUser(userData);

        // Save to localStorage
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));

        return true;
      } else {
        console.error('Login failed:', data.message);
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');

    // Call logout endpoint to invalidate token server-side
    if (token) {
      fetch(API_ENDPOINTS.AUTH.LOGOUT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }).catch(console.error);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    isLoading,
    needsSetup,
    checkSetupStatus,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
